/**
 * Utility Type Helpers
 * Type-safe helpers for common patterns
 */

import { ApiError } from './api.types';

// ============================================================================
// Error Handling Types
// ============================================================================

/**
 * Type guard to check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as ApiError).response === 'object'
  );
}

/**
 * Extract error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred';
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}

/**
 * Type-safe error handler for API calls
 */
export interface ErrorHandler {
  onError?: (message: string) => void;
  defaultMessage?: string;
}

/**
 * Handle API errors with type safety
 */
export function handleApiError(error: unknown, handler?: ErrorHandler): void {
  const message = getErrorMessage(error);
  const finalMessage = handler?.defaultMessage || message;

  if (handler?.onError) {
    handler.onError(finalMessage);
  } else {
    console.error('API Error:', finalMessage);
  }
}

// ============================================================================
// Type-safe Form Handling
// ============================================================================

/**
 * Extract form data with type safety
 */
export type FormDataExtractor<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K] extends number ? number : string;
};

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validator function type
 */
export type Validator<T> = (value: T) => string | null;

// ============================================================================
// Type-safe Local Storage
// ============================================================================

/**
 * Type-safe local storage get
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Type-safe local storage set
 */
export function setStorageItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

/**
 * Remove item from storage
 */
export function removeStorageItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to remove from localStorage:', error);
  }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if value is defined (not null or undefined)
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Check if value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Check if value is a valid number
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

/**
 * Check if array is non-empty
 */
export function isNonEmptyArray<T>(value: T[]): value is [T, ...T[]] {
  return Array.isArray(value) && value.length > 0;
}

// ============================================================================
// Async Utility Types
// ============================================================================

/**
 * Async state wrapper
 */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Initial async state
 */
export function createAsyncState<T>(initialData: T | null = null): AsyncState<T> {
  return {
    data: initialData,
    loading: false,
    error: null,
  };
}

/**
 * Update async state to loading
 */
export function setAsyncLoading<T>(state: AsyncState<T>): AsyncState<T> {
  return {
    ...state,
    loading: true,
    error: null,
  };
}

/**
 * Update async state with success
 */
export function setAsyncSuccess<T>(state: AsyncState<T>, data: T): AsyncState<T> {
  return {
    data,
    loading: false,
    error: null,
  };
}

/**
 * Update async state with error
 */
export function setAsyncError<T>(state: AsyncState<T>, error: string): AsyncState<T> {
  return {
    ...state,
    loading: false,
    error,
  };
}

// ============================================================================
// Debounce & Throttle Types
// ============================================================================

/**
 * Debounce function type
 */
export type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
};

/**
 * Create debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): DebouncedFunction<T> {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  };

  return debounced;
}

// ============================================================================
// Type-safe Object Utilities
// ============================================================================

/**
 * Pick properties from object with type safety
 */
export function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * Omit properties from object with type safety
 */
export function omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj };
  keys.forEach(key => {
    delete result[key];
  });
  return result;
}

/**
 * Deep clone with type safety
 */
export function deepClone<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => deepClone(item)) as unknown as T;
  }

  const cloned = {} as T;
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      cloned[key] = deepClone(value[key]);
    }
  }

  return cloned;
}
